import {
  HabitCompletionStatus,
  HabitStatus,
  HabitType,
  type HabitChecklistItem,
} from "@fitness-app/data-models/entities/Habit";

import { supabase } from "~/store/initializeStore";
import {
  forgetSimulatorWrite,
  getSimulatorWrite,
  logSimulatorWrite,
  SIMULATOR_TABLE,
  wasCreatedBySimulator,
} from "./simulatorWriteLog";

export type SimulatorHabit = {
  clientHabitId: string;
  templateId: string;
  name: string;
  type: HabitType;
  checklist: HabitChecklistItem[];
  /** Status odhaczenia na wybrany dzień, jeśli istnieje. */
  dayStatus: HabitCompletionStatus | null;
  dayCompletedItems: string[];
};

const asChecklist = (raw: unknown): HabitChecklistItem[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter((item): item is { id: unknown; label: unknown } => !!item && typeof item === "object")
    .map((item) => ({ id: String(item.id ?? ""), label: String(item.label ?? "") }))
    .filter((item) => item.id.length > 0);
};

/**
 * Aktywne nawyki podopiecznej wraz z odhaczeniem na wskazany dzień — odpowiednik tego, co
 * klientka widzi w swoim trackerze, tylko czytane po `traineeId` zamiast po zalogowanym userze.
 */
export const listTraineeHabits = async (traineeId: string, date: string): Promise<SimulatorHabit[]> => {
  const { data: rows, error } = await supabase
    .from("client_habit")
    .select("id, templateId, status, template:templateId(id, name, type, checklist)")
    .eq("traineeId", traineeId)
    .eq("status", HabitStatus.Active);

  if (error) {
    throw new Error(error.message);
  }

  const habits = (rows ?? []).filter((row) => row.template);

  if (habits.length === 0) {
    return [];
  }

  const { data: completions, error: completionsError } = await supabase
    .from("habit_completion")
    .select("clientHabitId, status, completedItems")
    .eq("traineeId", traineeId)
    .eq("date", date)
    .in(
      "clientHabitId",
      habits.map((row) => row.id),
    );

  if (completionsError) {
    throw new Error(completionsError.message);
  }

  const byHabit = new Map((completions ?? []).map((completion) => [completion.clientHabitId, completion]));

  return habits
    .map((row) => {
      const completion = byHabit.get(row.id);
      return {
        clientHabitId: row.id,
        templateId: row.templateId,
        name: row.template!.name,
        type: (row.template!.type as HabitType) ?? HabitType.Single,
        checklist: asChecklist(row.template!.checklist),
        dayStatus: (completion?.status as HabitCompletionStatus | undefined) ?? null,
        dayCompletedItems: Array.isArray(completion?.completedItems)
          ? completion.completedItems.map((item) => String(item))
          : [],
      } satisfies SimulatorHabit;
    })
    .sort((a, b) => a.name.localeCompare(b.name, "pl"));
};

type ToggleParams = {
  habit: SimulatorHabit;
  traineeId: string;
  date: string;
  createdBy: string;
  completed: boolean;
  mode: "single" | "bulk";
};

/**
 * Odhacza (lub cofa) nawyk na wskazany dzień dokładnie tą samą drogą co klientka: RPC
 * `toggle_habit_completion` zapisuje odhaczenie i przelicza streak w jednej transakcji.
 * Po zapisie dociągamy id powstałego wiersza, żeby trafił do rejestru symulatora.
 */
export const toggleHabitCompletion = async ({
  habit,
  traineeId,
  date,
  createdBy,
  completed,
  mode,
}: ToggleParams): Promise<void> => {
  const isGroup = habit.type === HabitType.Group;
  const completedItems = completed && isGroup ? habit.checklist.map((item) => item.id) : [];

  // Stan sprzed wywołania RPC: cofnięcie kasuje wiersz (więc id trzeba znać wcześniej), a przy
  // odhaczeniu decyduje o tym, czy symulator tworzy wpis, czy nadpisuje odhaczenie klientki.
  const { data: existingBefore, error: existingError } = await supabase
    .from("habit_completion")
    .select("id, status, completedItems")
    .eq("clientHabitId", habit.clientHabitId)
    .eq("traineeId", traineeId)
    .eq("date", date)
    .maybeSingle();

  // Nieudany odczyt musi przerwać akcję. Bez snapshotu zapis zostałby zaklasyfikowany jako
  // „insert", a czyszczenie skasowałoby odhaczenie, które zrobiła sama klientka.
  if (existingError) {
    throw new Error(`Nie udało się odczytać stanu nawyku: ${existingError.message}`);
  }

  // Cofanie odhaczenia, którego symulator nie stworzył, skasowałoby wpis klientki bezpowrotnie:
  // RPC kasuje wiersz, a razem z nim przepada wszystko, z czego dałoby się go odtworzyć.
  if (!completed && existingBefore?.id) {
    const entry = await getSimulatorWrite(SIMULATOR_TABLE.habitCompletion, existingBefore.id);

    if (entry && !wasCreatedBySimulator(entry)) {
      throw new Error(
        "To odhaczenie zrobiła klientka — symulator go nie cofa. Użyj „wyczyść dane symulatora”, żeby wrócić do jej stanu.",
      );
    }
  }

  const { error } = await supabase.rpc("toggle_habit_completion", {
    p_client_habit_id: habit.clientHabitId,
    p_trainee_id: traineeId,
    p_date: date,
    p_status: completed ? HabitCompletionStatus.Completed : null,
    p_completed_items: completedItems,
  });

  if (error) {
    throw new Error(error.message);
  }

  if (!completed) {
    if (existingBefore?.id) {
      await forgetSimulatorWrite(SIMULATOR_TABLE.habitCompletion, existingBefore.id);
    }
    return;
  }

  const { data: saved } = await supabase
    .from("habit_completion")
    .select("id")
    .eq("clientHabitId", habit.clientHabitId)
    .eq("traineeId", traineeId)
    .eq("date", date)
    .maybeSingle();

  if (saved?.id) {
    await logSimulatorWrite({
      traineeId,
      createdBy,
      tableName: SIMULATOR_TABLE.habitCompletion,
      rowId: saved.id,
      eventDate: date,
      mode,
      op: existingBefore ? "update" : "insert",
      before: existingBefore
        ? { status: existingBefore.status, completedItems: existingBefore.completedItems }
        : undefined,
      label: habit.name,
    });
  }
};
